import { Params, Router } from "@angular/router";
import swal, { SweetAlertIcon, SweetAlertResult } from "sweetalert2";

import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { ImageCheckPipe } from "./pipes/image-check.pipe";
import { Subscription, fromEvent, merge, of } from "rxjs";
import { map } from "rxjs/operators";
import { ActiveToast, ToastrService } from "ngx-toastr";

@Injectable()
export class Dialog {
  networkStatus: boolean = false;
  networkStatus$: Subscription = Subscription.EMPTY;
  toastrWsError: ActiveToast<any> | null;

  constructor(
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    public imageCheckPipe: ImageCheckPipe,
    private toastr: ToastrService
  ) {
    this.checkNetworkStatus();
  }
  private confirmButtonColor = "#63a10f";
  private cancelButtonColor = "#AAAAAA";

  checkNetworkStatus() {
    //Si no tenim connexió no mostrem CAP dialeg
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(
      of(null),
      fromEvent(window, "online"),
      fromEvent(window, "offline")
    )
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
      });
  }

  public getString(value: string, params?): string {
    let str = "";
    this.translateService.get(value, params).subscribe((res: string) => {
      return (str = res);
    });
    return str;
  }
  show(
    title: string,
    text: string,
    confirmButtonText: string = "Ok",
    cancelButtonText: string = "Cancel"
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text,
          showCancelButton: true,
          confirmButtonText: confirmButtonText,
          cancelButtonText: cancelButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
            cancelButton: "btn btn-secondary text-light mr-1",
          },
        })
      : null;
  }

  async showImage(
    img: string,
    override: string | null = null,
    defaultSrc: string | null = null,
    cancelButtonText: string = "Close"
  ): Promise<SweetAlertResult<any> | null> {
    const url = await this.imageCheckPipe.transform(
      img,
      "",
      override ?? "",
      defaultSrc ?? ""
    );
    return this.networkStatus
      ? swal.fire({
          imageUrl: url,
          showConfirmButton: false,
          showCancelButton: true,
          cancelButtonText: cancelButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
            cancelButton: "btn btn-secondary text-light mr-1",
          },
        })
      : null;
  }

  showError(
    title: string,
    message: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text: message,
          icon: "error",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
          },
        })
      : null;
  }
  showWarning(
    title: string,
    message: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text: message,
          icon: "warning",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
          },
        })
      : null;
  }
  showInfo(
    title: string,
    message: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text: message,
          icon: "info",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1",
          },
        })
      : null;
  }

  showAsk(
    title: string,
    text: string,
    confirmButtonText = "Ok",
    cancelButtonText = "Cancel"
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: confirmButtonText,
          cancelButtonText: cancelButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
            cancelButton: "btn btn-danger text-light mr-1",
          },
        })
      : null;
  }

  showAskWarning(
    title: string,
    text: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ok",
          cancelButtonText: "Cancel",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
            cancelButton: "btn btn-secondary text-light mr-1",
          },
        })
      : null;
  }

  showMsgTimer(
    title: string,
    message: string,
    route?: string,
    back?: boolean,
    timer?: number
  ): Promise<void> | null {
    return this.networkStatus
      ? swal
          .fire({
            title,
            text: message,
            icon: "success",
            showConfirmButton: false,
            timer,
          })
          .then(() => {
            if (route !== "" && route !== undefined) {
              void this.router.navigate([route]);
            } else {
              if (back) {
                this.location.back();
              }
            }
          })
      : null;
  }

  showMsgOk(
    title: string,
    message: string,
    route?: string,
    back?: boolean
  ): Promise<void> | null {
    return this.networkStatus
      ? swal
          .fire({
            title,
            text: message,
            icon: "success",
            confirmButtonText: "Ok",
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-primary text-light mr-1", //insert class here
              cancelButton: "btn btn-secondary text-light mr-1",
            },
          })
          .then(() => {
            if (route !== "" && route !== undefined) {
              void this.router.navigate([route]);
            } else {
              if (back) {
                this.location.back();
              }
            }
          })
      : null;
  }

  showMsgSuccess(
    title: string,
    message: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text: message,
          icon: "success",
          confirmButtonText: "Ok",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1", //insert class here
          },
        })
      : null;
  }

  showMsgNotOk(
    title: string,
    message: string
  ): Promise<SweetAlertResult<any>> | null {
    return this.networkStatus
      ? swal.fire({
          title,
          text: message,
          icon: "error",
          confirmButtonText: "Ok",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary text-light mr-1",
          },
        })
      : null;
  }

  showAskMultipleOptions(
    title: string,
    text: string,
    button1Text: string = "YES",
    button2Text: string = "NO",
    button3Text: string = "MAYBE",
    button1Color: string = "#83B719",
    button2Color: string = "#83B719",
    button3Color: string = "#d33"
  ) {
    return this.networkStatus
      ? swal.fire({
          title: title,
          text: text,
          icon: "warning",

          showDenyButton: true,
          showCancelButton: true,

          confirmButtonText: button1Text,
          denyButtonText: button2Text,
          cancelButtonText: button3Text,

          confirmButtonColor: button1Color,
          denyButtonColor: button2Color,
          cancelButtonColor: button3Color,
        })
      : null;
  }

  showAndNavigate(
    title: string,
    message: string,
    icon: SweetAlertIcon = "success",
    route?: string,
    back?: boolean
  ): Promise<void> | null {
    return this.networkStatus
      ? swal
          .fire({
            title,
            text: message,
            icon,
            confirmButtonText: "Ok",
            cancelButtonText: "Cancel",
            showCancelButton: true,
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-primary text-light mr-1", //insert class here
              cancelButton: "btn btn-secondary text-light mr-1",
            },
          })
          .then((action) => {
            if (action.isConfirmed) {
              this.location.back();
            }
          })
      : null;
  }

  showItemUploaded(
    title: string,
    message: string,
    route?: string,
    back?: boolean
  ) {
    let self = this;
    return this.networkStatus
      ? swal
          .fire({
            title: title,
            text: message,
            icon: "success",
            confirmButtonText: "Ok",
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-primary text-light mr-1",
            },
          })
          .then(function () {
            if (route != "" && route != undefined) {
              self.router.navigate([route]);
            } else {
              if (back) self.location.back();
            }
          })
      : null;
  }

  showAskWarningHTML(
    title: string,
    text: string,
    confirmButtonText: string = "YES",
    cancelButtonText: string = "NO"
  ) {
    let self = this;

    var lProductionTypes = {
      1: self.getString("PRODUCTION"),
      2: self.getString("PRE_SERIE"),
      3: self.getString("PROTOTIP"),
      4: self.getString("TEST"),
      5: self.getString("PRODUCTION_RESPONSIBLE"),
    };

    return this.networkStatus
      ? swal.fire({
          input: "select",
          inputOptions: lProductionTypes,
          title: title,
          html: text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#C9004C",
          confirmButtonText: this.getString(confirmButtonText),
          cancelButtonText: this.getString(cancelButtonText),
        })
      : null;
    // .catch(swal.noop);
  }

  showToastrError(title: string, message: string): void {
    this.toastr.error(message, title, {
      toastClass: "toast ngx-toastr",
      closeButton: true,
      timeOut: 60 * 1000,
    });
  }

  showToastrWarning(title: string, message: string): void {
    this.toastr.warning(message, title, {
      toastClass: "toast ngx-toastr",
      closeButton: true,
      timeOut: 60 * 1000,
    });
  }

  showToastrWsError(): void {
    if (!this.toastrWsError) {
      this.toastrWsError = this.toastr.error("Unknown Error", "WS ERROR", {
        toastClass: "toast ngx-toastr",
        closeButton: true,
        timeOut: 5000,
      });
    }
  }

  hideToastrWsError(): void {
    if (this.toastrWsError != null)
      this.toastr.remove(this.toastrWsError.toastId);
    this.toastrWsError = null;
  }
}
